import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeExchangeResponseMapping.widget';

/**
 * Builds the "Antwort-Zuordnungen" config table widget for cw.CwCaseTypeExchangeResponseMapping —
 * the response half of the exchange configuration (CW-EXCHANGE-V2, design §3.5). One row says which
 * element of an insurer response is sliced out (`responsePath` + `occurrence`) and where the value is
 * written back to (`targetType` + `targetKey`); it replaces the RESPONSE direction of the deleted
 * field-binding family.
 *
 * An EMPTY `operationKey` cell means the row applies to EVERY operation of the case type — which is
 * why the column is shown even though it is often blank. `overwritePolicy` states how far a row may go
 * on a protected target (Vorgang / Zeile / Side-Car): NEVER blocks and reports the write, IF_EMPTY
 * fills only an empty target, ALWAYS writes over a user entry — so the column belongs in the grid.
 *
 * WidgetFactory.createWidgetTableQl on an explicit, hand-curated QlQueryDto (root
 * cw.CwCaseTypeExchangeResponseMapping aliased `e`, one LEFT join to the owning caseType) plus a
 * curated WidgetForm for explicit German column headers. The `occurrence`/`targetType`/`dataType`/
 * `overwritePolicy` enum columns are selected as plain fields — the backend resolves their German
 * labels via @UiEnumValueInfo metadata, no FE enum mirror is needed for rendering. Sorted by
 * `orderIndex` (the deterministic processing order), not alphabetically.
 *
 * @param caseTypeId optional cw.CwCaseType id to scope the table to one CaseType (the normal
 *   embedding under that CaseType's BiPRO view); omit for the unscoped inspection table.
 */
export function buildCwCaseTypeExchangeResponseMappingTableWidget(caseTypeId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeExchangeResponseMapping',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'alias'},          // Alias (seed anchor, unique per CaseType)
                {name: 'mappingKey'},     // Mapping-Schlüssel (grossPremium/netPremium route the price)
                {name: 'operationKey'},   // Operation (empty = every operation)
                {name: 'responsePath'},   // Antwort-Pfad
                {name: 'targetType'},     // Ziel-Typ (Enum)
                {name: 'targetKey'},      // Zielschlüssel
                {name: 'occurrence'},     // Vorkommen (Enum)
                {name: 'dataType'},       // Datentyp (Enum)
                {name: 'overwritePolicy'}, // Überschreib-Richtlinie (Enum)
                {name: 'orderIndex'},     // Reihenfolge-Index
                {name: 'active'},         // Aktiv
            ],
            joins: [
                {
                    type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                },
            ],
        },
        fixedFilters: caseTypeId != null
            ? [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, caseTypeId)]
            : [],
        sortings: [new Sorting('e.orderIndex', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeExchangeResponseMapping config table — the response-side extraction rules per
     *   CaseType, curated columns (Vorgangstyp, Alias, Mapping-Schlüssel, Operation, Antwort-Pfad,
     *   Ziel-Typ, Zielschlüssel, Vorkommen, Datentyp, Überschreib-Richtlinie, Reihenfolge-Index, Aktiv).
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'Antwort-Zuordnungen',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeExchangeResponseMappingWidgetForm()),
    );
    widgetData.noDataText = 'Keine Antwort-Zuordnungen konfiguriert';
    return widgetData;
}

function buildCwCaseTypeExchangeResponseMappingWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'e.alias', uiLabel: 'Alias'},
        {field: 'e.mappingKey', uiLabel: 'Mapping-Schlüssel'},
        {field: 'e.operationKey', uiLabel: 'Operation'},
        {field: 'e.responsePath', uiLabel: 'Antwort-Pfad'},
        {field: 'e.targetType', uiLabel: 'Ziel-Typ'},
        {field: 'e.targetKey', uiLabel: 'Zielschlüssel'},
        {field: 'e.occurrence', uiLabel: 'Vorkommen'},
        {field: 'e.dataType', uiLabel: 'Datentyp'},
        {field: 'e.overwritePolicy', uiLabel: 'Überschreib-Richtlinie'},
        {field: 'e.orderIndex', uiLabel: 'Reihenfolge-Index'},
        {field: 'e.active', uiLabel: 'Aktiv'},
    ]);
}
